import React, { useMemo } from "react";
import { connect } from "react-redux";

import PointTypes from "types/PointTypes";

import MapUtils from "services/map/utils";

import MapPoint from "components/MapPoint";
import MapLabel from "components/MapLabel";

type Props = {
  loading: boolean;
  isPointSelected: Function;
  isLabelDisplayed: Function;
  hasLabelBackground: Function;
  currentFloor: number;
  map: {
    initialized: boolean;
    points: PointTypes[];
    selectedLayers: { id: string; ref_flow_points: [] }[];
  };
  options: {
    items: {
      area_id: string;
      point_id: string;
      reqFilters: { from: number; to: number };
      background: {
        value: number;
        total: number;
        out: number;
        in: number;
        config: {
          mainColor: string;
          unselectedColor: string;
          minColor: string;
          valueColors: {};
          unselectedValueColor: string;
        };
      };
    }[];
  };
  filters: {
    from: number;
    to: number;
  };
  onSelectLayers: Function;
  getLabelIcon?: Function;
  getLabel?: Function;
  getBackground?: Function;
  zoom: number;
  mapConfig: { labelsDisplayed?: boolean };
};

function PointsLayers({
  currentFloor,
  options,
  isPointSelected,
  isLabelDisplayed,
  hasLabelBackground,
  filters,
  loading,
  map,
  onSelectLayers,
  getLabelIcon,
  getLabel,
  getBackground,
  zoom,
  mapConfig,
}: Props) {
  const selectedLayers = useMemo(() => {
    return map?.selectedLayers || [];
  }, [map?.selectedLayers]);

  function handlePointClick(point: PointTypes) {
    const newSelectedLayer = {
      ...point,
      type: "point",
    };

    const alreadySelected = !!selectedLayers.find(
      (itm) => point?.id === itm?.id
    );

    const newSelectedLayers = alreadySelected
      ? selectedLayers.filter((itm) => point?.id !== itm?.id)
      : [...selectedLayers, newSelectedLayer];
    const isSelection = newSelectedLayers?.length > selectedLayers?.length;

    return onSelectLayers(newSelectedLayers, newSelectedLayer, isSelection);
  }

  function handleIsPointSelected(point: PointTypes): boolean {
    if (isPointSelected) {
      return isPointSelected(point);
    }

    return !!selectedLayers.find((layer) => {
      return layer?.id && layer?.id === point?.id;
    });
  }

  function handleIsLabelDisplayed(point: PointTypes) {
    if (isLabelDisplayed) {
      return (
        mapConfig?.labelsDisplayed &&
        isLabelDisplayed({ ...point, selected: handleIsPointSelected(point) })
      );
    }

    return mapConfig?.labelsDisplayed;
  }

  function handleHasLabelBackground(point: PointTypes) {
    if (hasLabelBackground) {
      return (
        mapConfig?.labelsDisplayed &&
        hasLabelBackground({ ...point, selected: handleIsPointSelected(point) })
      );
    }

    return true;
  }

  if (!map?.initialized || loading) {
    return null;
  }

  function handleIsInteractive(point: PointTypes) {
    return !["UNEQUIPPED", "VIRTUAL"].includes(point?.apiType);
  }

  return map?.points
    ?.filter((point) =>
      currentFloor || currentFloor === 0
        ? point.floor_level === currentFloor
        : point
    )
    .map((point) => {
      const label = getLabel ? getLabel(point) : null;
      const formattedOptions = options?.items?.find(
        (item) => item.point_id === point.id || point?.area_id
      );
      const background = getBackground
        ? getBackground(point)
        : formattedOptions?.background || point?.options?.background;
      return (
        <MapPoint
          key={point.id}
          disabled={point?.apiType === "VIRTUAL"}
          center={MapUtils.getNormalizedCoordinates(point?.location || [])}
          zoom={zoom}
          options={{
            ...(point?.options || {}),
            ...(formattedOptions || {}),
            withLabel: handleIsLabelDisplayed(point),
            withLabelBackground: handleHasLabelBackground({
              ...point,
              options: { ...point?.options, ...formattedOptions, background },
            }),
            apiType: point.apiType,
          }}
          label={
            label ? (
              <MapLabel layer={point} getIcon={getLabelIcon} label={label} />
            ) : null
          }
          onClick={() => handlePointClick(point)}
          selected={handleIsPointSelected(point)}
          interactive={handleIsInteractive(point)}
        />
      );
    });
}

const mapStateToProps = (state: any) => ({
  zoom: MapUtils.getZoom(state.Map),
  currentFloor: MapUtils.getFloor(state.Map),
  mapConfig: state.Map.config,
});

// @ts-ignore
export default connect(mapStateToProps)(PointsLayers);
